import { atom } from 'jotai';

import { CustomerPortalUserStatus } from '../_components/customer-portal/types';

import { settingsRouteParamAtom } from './settingsModule.state';

export const $cpInvitationId = settingsRouteParamAtom(
  ['/customer-portal/users/:invitationId'],
  'invitationId',
);

export type CustomerPortalUserDetailsTab =
  | 'contact-info'
  | 'roles'
  | 'locations';

export const $cpUserDetailsTab =
  atom<CustomerPortalUserDetailsTab>('contact-info');

export interface CustomerPortalCurrentUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  company: string;
  contactLocation: string;
  status: CustomerPortalUserStatus;
  accountId: string;
  createdAt: string;
}

export const $cpCurrentUser = atom<CustomerPortalCurrentUser | null>(null);

export interface CustomerPortalUserFormState {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  contactLocation: string;
}

const initialFormState: CustomerPortalUserFormState = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  company: '',
  contactLocation: '',
};

export const $cpUserForm = atom<CustomerPortalUserFormState>(initialFormState);

export const $updateCpUserForm = atom(
  null,
  (get, set, update: Partial<CustomerPortalUserFormState>) => {
    set($cpUserForm, { ...get($cpUserForm), ...update });
  },
);

export const $resetCpUserForm = atom(null, (_get, set) => {
  set($cpUserForm, initialFormState);
});

export const $isCpUserFormDirty = atom((get) => {
  const currentUser = get($cpCurrentUser);
  const form = get($cpUserForm);

  if (!currentUser) return false;

  return (
    form.firstName !== currentUser.firstName ||
    form.lastName !== currentUser.lastName ||
    form.email !== currentUser.email ||
    form.phone !== (currentUser.phone ?? '')
  );
});

export const $initCpUserForm = atom(null, (get, set) => {
  const currentUser = get($cpCurrentUser);
  if (currentUser) {
    set($cpUserForm, {
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      email: currentUser.email,
      phone: currentUser.phone ?? '',
      company: currentUser.company,
      contactLocation: currentUser.contactLocation,
    });
  }
});

export interface CpUserRoleAssignment {
  roleId: string;
  roleName: string;
  userCount: number;
  assignedFacilityIds: string[];
  lastUpdatedAt: string | null;
  lastUpdatedBy: string | null;
}

export interface CpRawAssignmentEntry {
  id: string;
  roleId: string;
  facilityId: string;
}

export interface CpUserRoleFormState {
  assignments: CpUserRoleAssignment[];
  selectedRoleId: string | null;
  draftFacilityIds: string[];
  isDirty: boolean;
  rawAssignments: CpRawAssignmentEntry[];
}

const initialRoleFormState: CpUserRoleFormState = {
  assignments: [],
  selectedRoleId: null,
  draftFacilityIds: [],
  isDirty: false,
  rawAssignments: [],
};

export const $cpUserRoleForm = atom<CpUserRoleFormState>(initialRoleFormState);

export const $updateCpUserRoleForm = atom(
  null,
  (get, set, update: Partial<CpUserRoleFormState>) => {
    set($cpUserRoleForm, { ...get($cpUserRoleForm), ...update });
  },
);

export const $resetCpUserRoleForm = atom(null, (_get, set) => {
  set($cpUserRoleForm, initialRoleFormState);
});

export const $cpSelectRole = atom(null, (get, set, roleId: string | null) => {
  const state = get($cpUserRoleForm);
  const assignment = state.assignments.find((a) => a.roleId === roleId);

  set($cpUserRoleForm, {
    ...state,
    selectedRoleId: roleId,
    draftFacilityIds: assignment?.assignedFacilityIds ?? [],
    isDirty: false,
  });
});

export const $cpUpdateDraftFacilities = atom(
  null,
  (get, set, facilityIds: string[]) => {
    const state = get($cpUserRoleForm);
    const assignment = state.assignments.find(
      (a) => a.roleId === state.selectedRoleId,
    );
    const originalFacilityIds = assignment?.assignedFacilityIds ?? [];

    const isDirty =
      facilityIds.length !== originalFacilityIds.length ||
      facilityIds.some((id) => !originalFacilityIds.includes(id));

    set($cpUserRoleForm, {
      ...state,
      draftFacilityIds: facilityIds,
      isDirty,
    });
  },
);

export const $cpDiscardRoleDraftChanges = atom(null, (get, set) => {
  const state = get($cpUserRoleForm);
  const assignment = state.assignments.find(
    (a) => a.roleId === state.selectedRoleId,
  );

  set($cpUserRoleForm, {
    ...state,
    draftFacilityIds: assignment?.assignedFacilityIds ?? [],
    isDirty: false,
  });
});
